import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AppAccessService } from '@/core/services/app-access.service';
import { MessageService } from 'primeng/api';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * Clave del `<p-toast>` donde se muestran los mensajes de autorización.
 *
 * Va en un toast propio (abajo-derecha, declarado en `app.component.ts`) y no en el global porque
 * muchas pantallas montan su propio `<p-toast>` arriba-derecha con una instancia LOCAL de
 * `MessageService`. Ese toast local sigue mostrando el error genérico de la pantalla ("No se
 * pudieron cargar los conceptos"); si ambos cayeran en la misma esquina, los dos contenedores
 * fixed se solapan y el mensaje que explica el problema real queda tapado.
 */
export const AUTH_TOAST_KEY = 'auth-error';

/**
 * Traduce 401/403 a un mensaje que el usuario pueda accionar.
 *
 * ⚠️ Por qué existe: la app no tenía NINGÚN interceptor de errores. Cada componente hacía su
 * propio toast genérico ("No se pudieron cargar los conceptos"), indistinguible de un 500 o de
 * un backend caído. Un usuario cuyo rol no alcanzaba veía la pantalla cargar vacía sin ninguna
 * pista de que el problema era de permisos — hubo que abrir devtools y decodificar el JWT a mano
 * para diagnosticarlo.
 *
 * El backend ahora manda `{ error, message, requiredRoles }` en sus 403 (ver
 * `Utils/ForbiddenResultExtensions.cs` y el 403 del middleware), así que acá se muestra el
 * mensaje real en vez de inventar uno.
 *
 * Clase, no función: `app.config.ts` usa `withInterceptorsFromDi()` + `HTTP_INTERCEPTORS`.
 */
@Injectable()
export class AuthErrorInterceptor implements HttpInterceptor {
    private readonly messages = inject(MessageService);
    private readonly access = inject(AppAccessService);

    /** Última vez que se mostró cada mensaje, para no repetirlo N veces. */
    private readonly lastShown = new Map<string, number>();

    /**
     * Ventana de deduplicación. Una sola pantalla dispara varias peticiones en paralelo (el caso
     * reportado lanzó 3 a la vez), y todas fallan con el mismo 403: sin esto son 3 toasts
     * idénticos apilados.
     */
    private static readonly DEDUPE_WINDOW_MS = 3000;

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return next.handle(req).pipe(
            catchError((error: unknown) => {
                if (error instanceof HttpErrorResponse) {
                    if (error.status === 403) {
                        // Cuenta SIN NINGÚN rol: no es "esta pantalla no", es "nada". Un toast no
                        // sirve — la pantalla que quedó detrás está vacía y todas las demás también.
                        // Se la manda a la pantalla de bloqueo, desde donde puede cambiar de cuenta.
                        // Cubre lo que `appAccessGuard` no puede ver: roles revocados a mitad de
                        // sesión (el token viejo sigue trayéndolos hasta que se renueve) y un token
                        // cuyo claim `roles` no coincide con lo que el backend evalúa.
                        if (AuthErrorInterceptor.reportsNoRoles(error) || this.access.hasNoRoles()) {
                            this.access.expel();
                            return throwError(() => error);
                        }

                        this.notifyOnce('error', 'Sin permisos', this.describeForbidden(error));
                    } else if (error.status === 401) {
                        this.notifyOnce('warn', 'Sesión expirada', 'Su sesión ya no es válida. Vuelva a iniciar sesión para continuar.');
                    }
                }

                // Re-lanzar siempre: los handlers `error:` de los componentes siguen corriendo
                // (spinners, estados vacíos, limpieza). Este interceptor solo agrega el mensaje.
                return throwError(() => error);
            })
        );
    }

    /**
     * ¿El 403 dice que el usuario no tiene NINGÚN rol?
     *
     * El middleware del backend (`AutomaticRoleAuthorizationMiddleware`) arma el mensaje como
     * `Usuario no tiene permisos para GET /ruta. Roles del usuario: [Talento.Admin]. Política
     * requerida: ReadPolicy`, con los corchetes vacíos cuando la lista está vacía. Se lee esa lista
     * y no solo el token porque es la única que refleja lo que el backend REALMENTE evaluó: si el
     * claim del token y el criterio del backend discrepan, gana el backend.
     */
    private static reportsNoRoles(error: HttpErrorResponse): boolean {
        const body = error.error;
        const message = typeof body === 'string' ? body : (body?.message as string | undefined);
        return /Roles del usuario:\s*\[\s*\]/i.test(message ?? '');
    }

    private describeForbidden(error: HttpErrorResponse): string {
        // El cuerpo puede venir como objeto (JSON parseado) o como string si el content-type no
        // era JSON. Antes venía vacío — Forbid() no manda cuerpo — de ahí el fallback.
        const body = error.error;
        const message =
            typeof body === 'string' && body.trim().length > 0 && !body.trim().startsWith('<')
                ? body.trim()
                : (body?.message as string | undefined);

        const base = message?.trim() || 'Su cuenta no tiene permiso para esta acción.';
        const required = body?.requiredRoles;

        if (Array.isArray(required) && required.length > 0) {
            return `${base} Roles que dan acceso: ${required.join(', ')}.`;
        }
        return base;
    }

    private notifyOnce(severity: 'error' | 'warn', summary: string, detail: string): void {
        const key = `${severity}|${summary}|${detail}`;
        const now = Date.now();
        const previous = this.lastShown.get(key);

        if (previous !== undefined && now - previous < AuthErrorInterceptor.DEDUPE_WINDOW_MS) {
            return;
        }

        this.lastShown.set(key, now);
        this.messages.add({ key: AUTH_TOAST_KEY, severity, summary, detail, life: 8000 });

        // El mapa solo se usa para deduplicar dentro de la ventana; se poda para que una sesión
        // larga con muchos mensajes distintos no lo haga crecer sin límite.
        if (this.lastShown.size > 50) {
            for (const [k, t] of this.lastShown) {
                if (now - t >= AuthErrorInterceptor.DEDUPE_WINDOW_MS) this.lastShown.delete(k);
            }
        }
    }
}
